import { useState, useEffect } from "react";
import Avatar from "@mui/material/Avatar";
import { updatePassword } from "firebase/auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faShieldHalved,
	faWallet,
	faSackDollar,
	faReceipt,
	faArrowTrendUp,
	faCaretDown,
	faKey,
	faUserCheck,
	faCircleInfo,
	faMinus,
} from "@fortawesome/free-solid-svg-icons";
import { Line, Doughnut } from "react-chartjs-2";
import {
	Chart as ChartJS,
	ArcElement,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import AlertMessage from "../../Components/Alert/Alert";
import { useOutletContext } from "react-router-dom";
import "./profile.css";

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	ArcElement
);

const getDaysInCurrentMonth = () => {
	const now = new Date();
	const year = now.getFullYear();
	const month = now.getMonth();
	const days = new Date(year, month + 1, 0).getDate(); // Последний день месяца
	return Array.from({ length: days }, (_, i) => i + 1); // Массив от 1 до "days"
};

const Profile = () => {
	const { user } = useOutletContext();
	const [passwordInputs, setPasswordInputs] = useState({
		newPassword: "",
		confirmPassword: "",
	});
	const [alertParams, setAlertParams] = useState({
		open: false,
		message: "",
		type: "",
	});
	const [processedData, setProcessedData] = useState({
		orders: [],
		exchanges: [],
		deposits: [],
	});

	useEffect(() => {
		const processData = (dayKey, valueKey) => {
			const tempArray = [];
			tempArray[user[dayKey] - 1] = user[valueKey];

			// Заполнение пропущенных значений нулями
			for (let i = 0; i <= user[dayKey] - 1; i++) {
				if (tempArray[i] === undefined) {
					tempArray[i] = 0;
				}
			}

			return tempArray;
		};
		const newData = {
			orders: processData("orderDay", "orders"),
			exchanges: processData("exchangesDay", "exchanges"),
			deposits: processData("depositsDay", "chartDeposits"),
		};
		setProcessedData(newData);
	}, [user]);

	const handleClose = () => {
		setAlertParams({ ...alertParams, open: false });
	};
	const daysInMonth = getDaysInCurrentMonth();
	const data1 = {
		labels: ["Прибыль", "Убыток"], // Названия параметров
		datasets: [
			{
				label: "Уровень успеха",
				data: [user.profitableTrades, user.unprofitableTrades], // Значения для двух параметров
				backgroundColor: ["#36a2eb", "#ff6384"], // Цвета секторов
				hoverBackgroundColor: ["#36a2ebaa", "#ff6384aa"], // Цвета при наведении
				borderWidth: 1, // Толщина границ
			},
		],
	};
	const options1 = {
		responsive: true, // Адаптивность
		plugins: {
			legend: {
				position: "top", // Позиция легенды
				labels: {
					font: {
						size: 16, // Размер шрифта легенды
					},
					color: "#e3e1e9", // Цвет текста легенды
				},
			},
			tooltip: {
				bodyFont: {
					size: 14, // Размер шрифта тултипа
				},
				callbacks: {
					label: (context) => {
						const label = context.label || "";
						const value = context.raw;
						const total = context.dataset.data.reduce((a, b) => a + b, 0);
						const percentage = ((value / total) * 100)?.toFixed(2) + "%";
						return `${label}: ${value} (${percentage})`;
					},
				},
			},
			title: {
				display: true,
				text: "Уровень успеха", // Заголовок графика
				font: {
					size: 20,
					weight: "bold",
				},
				color: "#e3e1e9", // Цвет текста заголовка
				padding: {
					bottom: 20,
				},
			},
		},
	};
	const data = {
		labels: daysInMonth, // Подписи на оси X
		datasets: [
			{
				label: "Сделки",
				data: processedData.orders, // Значения для линии 1
				borderColor: "rgb(255, 99, 132)", // Цвет линии
				backgroundColor: "rgba(255, 99, 132, 0.2)", // Цвет заливки
			},
			{
				label: "Выводы",
				data: [], // Значения для линии 2
				borderColor: "rgb(54, 162, 235)",
				backgroundColor: "rgba(54, 162, 235, 0.2)",
			},
			{
				label: "Депозиты",
				data: processedData.deposits, // Значения для линии 3
				borderColor: "rgb(75, 192, 192)",
				backgroundColor: "rgba(75, 192, 192, 0.2)",
			},
			{
				label: "Обмены",
				data: processedData.exchanges, // Значения для линии 4
				borderColor: "rgb(255, 205, 86)",
				backgroundColor: "rgba(255, 205, 86, 0.2)",
			},
		],
	};
	const options = {
		responsive: true, // Адаптивность
		plugins: {
			legend: {
				position: "top", // Позиция легенды
				labels: {
					font: {
						size: 16, // Размер шрифта легенды
					},
					color: "#e3e1e9", // Цвет текста легенды
				},
			},
			title: {
				display: true,
				text: "График баланса", // Заголовок графика
				font: {
					size: 30, // Размер шрифта заголовка
					weight: "bold", // Жирность текста
				},
				color: "#e3e1e9", // Цвет текста заголовка
				padding: {
					bottom: 40, // Отступ снизу заголовка (между заголовком и графиком)
				},
			},
		},
		scales: {
			x: {
				ticks: {
					color: "#e3e1e9", // Цвет текста на оси X
					font: {
						size: 14, // Размер текста на оси X
					},
				},
			},
			y: {
				ticks: {
					color: "#e3e1e9", // Цвет текста на оси Y
					font: {
						size: 14, // Размер текста на оси Y
					},
				},
			},
		},
	};

	const [expanded, setExpanded] = useState(false);

	const handleChange = (panel) => (event, isExpanded) => {
		setExpanded(isExpanded ? panel : false);
	};

	const handleChangePassword = (e) => {
		const { name, value } = e.target;
		setPasswordInputs({
			...passwordInputs,
			[name]: value,
		});
	};

	const handlePasswordChange = async () => {
		if (passwordInputs.newPassword !== passwordInputs.confirmPassword) {
			alert("Пароли не совпадают");
			return;
		}

		if (!passwordInputs.newPassword) {
			alert("Пароль не может быть пустым");
			return;
		}

		try {
			await updatePassword(user, passwordInputs.newPassword);
			setAlertParams({
				open: true,
				type: "success",
				message: "Пароль успешно изменен",
			});
			setPasswordInputs({ newPassword: "", confirmPassword: "" }); // Очистить поля
		} catch (error) {
			if (error.code === "auth/requires-recent-login") {
				setAlertParams({
					open: true,
					type: "error",
					message: "Необходима повторная авторизация",
				});
				// Здесь можно вызвать метод для выхода и повторной авторизации
			} else {
				setAlertParams({
					open: true,
					type: "error",
					message: error.message,
				});
			}
		}
	};

	return (
		<div className="profile">
			<AlertMessage
				open={alertParams.open}
				message={alertParams.message}
				type={alertParams.type}
				onClose={handleClose}
			/>
			<div className="profile-header">
				<div className="profile-header-info">
					<Avatar
						sx={{
							width: 70,
							height: 70,
							fontSize: "20px",
							backgroundColor: "#b7c4ff",
						}}
					>
						VA
					</Avatar>
					<div className="profile-header-info-name">
						<p className="profile-header-info-name-name">Vlasov Andy</p>
						<p className="profile-header-info-name-email">{user.email}</p>
					</div>
				</div>
				<div className="profile-header-verify">
					<p className="profile-header-verify-title">Профиль завершен на 45%</p>
					<div className="profile-header-verify-bar-wrapper">
						<div className="profile-header-verify-bar">
							<div className="profile-header-verify-bar-fill"></div>
						</div>
						<div className="profile-header-verify-bar-icon">
							<FontAwesomeIcon icon={faShieldHalved} />
						</div>
					</div>
				</div>
			</div>
			<div className="profile-body">
				<div className="profile-body-left">
					<div className="profile-body-left-info">
						<div className="profile-body-left-info-item">
							<div className="profile-body-left-info-item-title">
								<div className="profile-body-left-info-item-title-icon">
									<FontAwesomeIcon icon={faWallet} />
								</div>
								<p className="profile-body-left-info-item-title-text">Общий баланс</p>
							</div>
							<div className="profile-body-left-info-item-wrapper">
								<div className="profile-body-left-info-item-wrapper-title">
									<p className="profile-body-left-info-item-wrapper-title-text">USD</p>
									<p className="profile-body-left-info-item-wrapper-title-subtext">
										в USD
									</p>
								</div>
								<div className="profile-body-left-info-item-wrapper-value">
									<p className="profile-body-left-info-item-wrapper-value-text">
										$ {user?.balance?.toFixed(1)}
									</p>
									<p className="profile-body-left-info-item-wrapper-value-subtext">
										$ {user?.balance?.toFixed(1)}
									</p>
								</div>
							</div>
						</div>
						<div className="profile-body-left-info-item">
							<div className="profile-body-left-info-item-title">
								<div className="profile-body-left-info-item-title-icon">
									<FontAwesomeIcon icon={faSackDollar} />
								</div>
								<p className="profile-body-left-info-item-title-text">Общие депозиты</p>
							</div>
							<div className="profile-body-left-info-item-wrapper">
								<div className="profile-body-left-info-item-wrapper-title">
									<p className="profile-body-left-info-item-wrapper-title-text">USD</p>
									<p className="profile-body-left-info-item-wrapper-title-subtext">
										в USD
									</p>
								</div>
								<div className="profile-body-left-info-item-wrapper-value">
									<p className="profile-body-left-info-item-wrapper-value-text">
										$ {user?.deposits?.toFixed(1)}
									</p>
									<p className="profile-body-left-info-item-wrapper-value-subtext">
										$ {user?.deposits?.toFixed(1)}
									</p>
								</div>
							</div>
						</div>
						<div className="profile-body-left-info-item">
							<div className="profile-body-left-info-item-title">
								<div className="profile-body-left-info-item-title-icon">
									<FontAwesomeIcon icon={faReceipt} />
								</div>
								<p className="profile-body-left-info-item-title-text">
									Прибыльные сделки
								</p>
							</div>
							<div className="profile-body-left-info-item-wrapper">
								<div className="profile-body-left-info-item-wrapper-title">
									<p className="profile-body-left-info-item-wrapper-title-text">
										Прибыльные / Общие сделки
									</p>
								</div>
								<div className="profile-body-left-info-item-wrapper-value">
									<p className="profile-body-left-info-item-wrapper-value-text">
										{user.profitableTrades} /{" "}
										{user.profitableTrades + user.unprofitableTrades}
									</p>
								</div>
							</div>
						</div>
						<div className="profile-body-left-info-item">
							<div className="profile-body-left-info-item-title">
								<div className="profile-body-left-info-item-title-icon">
									<FontAwesomeIcon icon={faArrowTrendUp} />
								</div>
								<p className="profile-body-left-info-item-title-text">Общий PNL</p>
							</div>
							<div className="profile-body-left-info-item-wrapper">
								<div className="profile-body-left-info-item-wrapper-title">
									<p className="profile-body-left-info-item-wrapper-title-text">USD</p>
									<p className="profile-body-left-info-item-wrapper-title-subtext">
										в USD
									</p>
								</div>
								<div className="profile-body-left-info-item-wrapper-value">
									<p className="profile-body-left-info-item-wrapper-value-text">
										$ {user?.pnl?.toFixed(1)}
									</p>
									<p className="profile-body-left-info-item-wrapper-value-subtext">
										$ {user?.pnl?.toFixed(1)}
									</p>
								</div>
							</div>
						</div>
					</div>
					<div className="profile-body-left-chart">
						{user.chartDeposits ||
						user.exchanges ||
						user.orders ||
						user.withdrawals ? (
							<Line data={data} options={options} />
						) : (
							<div className="profile-table-rows">
								<img src="/icons/cloud.svg" alt="empty-icon" />
								<p className="accounts-table-rows-empty">Не найдено данных</p>
							</div>
						)}
					</div>
				</div>
				<div className="profile-body-right">
					<div className="profile-body-right-info">
						{user.profitableTrades || user.unprofitableTrades ? (
							<Doughnut data={data1} options={options1} width={300} height={300} />
						) : (
							<div className="profile-table-rows">
								<img src="/icons/cloud.svg" alt="empty-icon" />
								<p className="accounts-table-rows-empty">Не найдено данных</p>
							</div>
						)}
					</div>
					<div className="profile-body-right-settings">
						<div className="profile-body-right-settings-title">Настройки</div>
						<Accordion
							expanded={expanded === "panel1"}
							onChange={handleChange("panel1")}
						>
							<AccordionSummary
								expandIcon={<FontAwesomeIcon icon={faCaretDown} />}
								aria-controls="panel1bh-content"
								id="panel1bh-header"
								sx={{ fontSize: "20px", py: 1, borderBottom: "1px solid #90909a29" }}
							>
								<FontAwesomeIcon icon={faKey} style={{ marginRight: "10px" }} />
								Пароль
							</AccordionSummary>
							<AccordionDetails
								sx={{
									display: "flex",
									flexDirection: "column",
									justifyContent: "center",
									alignItems: "center",
									gap: "20px",
									pt: "20px",
								}}
							>
								<TextField
									id="new-basic"
									label="Новый пароль"
									variant="outlined"
									sx={{ width: "320px" }}
									name="newPassword"
									value={passwordInputs.newPassword}
									onChange={handleChangePassword}
								/>
								<TextField
									id="confirm-basic"
									label=" Подтвердите пароль"
									variant="outlined"
									sx={{ width: "320px" }}
									name="confirmPassword"
									value={passwordInputs.confirmPassword}
									onChange={handleChangePassword}
								/>
								<Button variant="contained" onClick={handlePasswordChange}>
									Изменить пароль
								</Button>
							</AccordionDetails>
						</Accordion>
						<Accordion
							expanded={expanded === "panel2"}
							onChange={handleChange("panel2")}
						>
							<AccordionSummary
								expandIcon={<FontAwesomeIcon icon={faCaretDown} />}
								aria-controls="panel1bh-content"
								id="panel1bh-header"
								sx={{ fontSize: "20px", py: 1, borderBottom: "1px solid #90909a29" }}
							>
								<FontAwesomeIcon icon={faUserCheck} style={{ marginRight: "10px" }} />
								Верефикация Аккаунт
							</AccordionSummary>
							<AccordionDetails
								sx={{
									display: "flex",
									flexDirection: "column",
									justifyContent: "center",
									alignItems: "center",
									gap: "20px",
									pt: "20px",
								}}
							>
								<div className="profile-body-right-settings-verify">
									<div className="profile-body-right-settings-verify-title">
										<p className="profile-body-right-settings-verify-title-text">
											Проверка личности
										</p>
										<FontAwesomeIcon icon={faCircleInfo} style={{ color: "#FF7F7F" }} />
									</div>
									<p className="profile-body-right-settings-verify-subtext">
										Все новые пользователи должны быть верифицированы, чтобы получить
										доступ к продуктам и услугам CRM, включая совершение сделок и вывод
										криптовалюты
									</p>
									<ul className="profile-body-right-settings-verify-list">
										<li className="profile-body-right-settings-verify-list-item">
											<FontAwesomeIcon icon={faMinus} />
											Подтверждение действительности удостоверения личности
										</li>
										<li className="profile-body-right-settings-verify-list-item">
											<FontAwesomeIcon icon={faMinus} />
											Подтверждение вашего Селфи
										</li>
										<li className="profile-body-right-settings-verify-list-item">
											<FontAwesomeIcon icon={faMinus} />
											Внесение депозитной суммы на резервный счёт
										</li>
									</ul>
									<Button
										variant="contained"
										sx={{ width: "320px", display: "block", margin: "0 auto" }}
										onClick={() =>
											setAlertParams({
												open: true,
												type: "error",
												message: "Активируйте аккаунт внеся первый депозит",
											})
										}
									>
										Пройти верефикацию
									</Button>
								</div>
							</AccordionDetails>
						</Accordion>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Profile;
